import React, {useEffect, useMemo, useState} from 'react';
import {Tag} from "antd";
import {useCollection} from "react-firebase-hooks/firestore";
import {arrayUnion, collection, doc, getFirestore, orderBy, query, serverTimestamp, updateDoc} from "firebase/firestore";
import {getAuth} from "firebase/auth";
import {useToast} from "@chakra-ui/react";
import moment from 'moment';
import {Link} from "react-router-dom";
import Firebase from "../../Firebase/Firebase";
import {sendSupportMessage} from "./SupportChats";

const db = getFirestore(Firebase);

const CATEGORY_LABEL: Record<string, string> = {
    payment: 'Payment', order_delivery: 'Order / delivery', refund: 'Refund', account: 'Account',
    seller_payout: 'Seller payout', app_problem: 'App problem', other: 'Other', suggestion: 'Suggestion',
};

/** Problem flow and suggestion flow, in order. */
const PROBLEM_STATUSES = ['open', 'in_progress', 'waiting', 'resolved', 'closed'] as const;
const SUGGESTION_STATUSES = ['received', 'under_review', 'planned', 'done', 'not_planned'] as const;
const STATUS_LABEL: Record<string, string> = {
    open: 'Open', in_progress: 'In progress', waiting: 'Waiting for you', resolved: 'Resolved', closed: 'Closed',
    received: 'Received', under_review: 'Under review', planned: 'Planned', done: 'Done', not_planned: 'Not planned',
};
const STATUS_COLOR: Record<string, string> = {
    open: 'red', in_progress: 'orange', waiting: 'blue', resolved: 'green', closed: 'default',
    received: 'red', under_review: 'orange', planned: 'blue', done: 'green', not_planned: 'default',
};
const DONE_STATES = new Set(['resolved', 'closed', 'done', 'not_planned']);

const btn: React.CSSProperties = {
    fontSize: 12, border: '1px solid #c7d2fe', borderRadius: 6, padding: '4px 10px',
    cursor: 'pointer', background: '#eef2ff', color: '#29327b',
};
const fmt = (v: any) => (v ? moment(typeof v.toDate === 'function' ? v.toDate() : v).format('DD MMM YYYY, HH:mm') : '');
const age = (v: any) => (v ? moment(typeof v.toDate === 'function' ? v.toDate() : v).fromNow() : '');

/** What the user is told in their Support chat when the status changes. */
function statusMessage(t: any, status: string, reason: string) {
    const head = `🎫 #${t.ticketNo}`;
    if (t.type === 'suggestion') {
        const map: Record<string, string> = {
            received: 'We received your suggestion — thank you!',
            under_review: 'We are reviewing your suggestion.',
            planned: 'Good news — your suggestion is planned.',
            done: 'Your suggestion is now live in Shopped. Thank you!',
            not_planned: `We won't be doing this one for now.${reason ? ` ${reason}` : ''} Thank you for the idea.`,
        };
        return `${head}: ${map[status] ?? STATUS_LABEL[status]}`;
    }
    const map: Record<string, string> = {
        open: 'is open again.',
        in_progress: 'is now in progress — we are working on it.',
        waiting: 'needs a reply from you — please answer here.',
        resolved: `is resolved.${reason ? ` ${reason}` : ''} Reply here if anything is still wrong.`,
        closed: 'is closed.',
    };
    return `${head} ${map[status] ?? STATUS_LABEL[status]}`;
}

function TicketDetail({id, t}: {id: string; t: any}) {
    const toast = useToast();
    const [note, setNote] = useState(t.adminNote ?? '');
    const [reason, setReason] = useState('');
    useEffect(() => { setNote(t.adminNote ?? ''); setReason(''); }, [id, t.adminNote]);
    const statuses: readonly string[] = t.type === 'suggestion' ? SUGGESTION_STATUSES : PROBLEM_STATUSES;
    const admin = getAuth(Firebase).currentUser?.email ?? null;

    const setStatus = async (status: string) => {
        try {
            await updateDoc(doc(db, 'supportTickets', id), {
                status,
                statusReason: reason.trim() || null,
                updatedAt: serverTimestamp(),
                updatedBy: admin,
                history: arrayUnion({status, at: new Date(), by: admin, reason: reason.trim() || null}),
            });
            // Tell the user in their Support chat (this is what sends the push).
            await sendSupportMessage(t.userId, statusMessage(t, status, reason.trim()), {name: t.userName});
            toast({position: 'top', title: `Now: ${STATUS_LABEL[status]}`, status: 'success', duration: 1500});
            setReason('');
        } catch (e: any) {
            toast({position: 'top', title: e.message, status: 'error', duration: 3000});
        }
    };

    return (
        <div style={{border: '1px solid #eee', borderRadius: 10, padding: 16, background: '#fff'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8}}>
                <div>
                    <div style={{fontWeight: 800, fontSize: 16}}>#{t.ticketNo} · {CATEGORY_LABEL[t.category] ?? t.category}</div>
                    <div style={{fontSize: 12, color: '#666', marginTop: 2}}>
                        {t.userName || t.userId} <Tag color={t.userRole === 'seller' ? 'purple' : 'cyan'}>{t.userRole === 'seller' ? 'Seller' : 'Buyer'}</Tag>
                        · opened {fmt(t.createdAt)} · <Link to={`/buyers/${t.userId}`}>account</Link>
                    </div>
                    {t.orderNo && <div style={{fontSize: 12, color: '#666', marginTop: 2}}>Order: <b>{t.orderNo}</b> ({t.orderId})</div>}
                </div>
                <Tag color={STATUS_COLOR[t.status]} style={{alignSelf: 'flex-start'}}>{STATUS_LABEL[t.status]}</Tag>
            </div>

            <div style={{whiteSpace: 'pre-wrap', fontSize: 14, margin: '14px 0', lineHeight: 1.45}}>{t.description}</div>

            <div style={{fontSize: 12, fontWeight: 700, color: '#29327b', marginBottom: 6}}>Change status (the user is notified in their Support chat)</div>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder={t.type === 'suggestion' ? 'Optional note to the user (e.g. why not planned)' : 'Optional note to the user (e.g. what was fixed)'}
                   style={{width: '100%', padding: '6px 8px', borderRadius: 8, border: '1px solid #eee', fontSize: 13, marginBottom: 8}}/>
            <div style={{display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14}}>
                {statuses.filter((s) => s !== t.status).map((s) => (
                    <button key={s} style={btn} onClick={() => setStatus(s)}>{STATUS_LABEL[s]}</button>
                ))}
            </div>

            <div style={{display: 'flex', gap: 8, marginBottom: 14}}>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (only admins see this)"
                       style={{flex: 1, padding: '6px 8px', borderRadius: 8, border: '1px solid #eee', fontSize: 13}}/>
                <button style={btn} onClick={() => updateDoc(doc(db, 'supportTickets', id), {adminNote: note.trim(), updatedAt: serverTimestamp(), updatedBy: admin})
                    .then(() => toast({position: 'top', title: 'Note saved', status: 'success', duration: 1200}))}>Save note</button>
            </div>

            <div style={{fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 4}}>History</div>
            {(t.history ?? []).map((h: any, i: number) => (
                <div key={i} style={{fontSize: 12, color: '#666'}}>
                    {fmt(h.at)} — {STATUS_LABEL[h.status] ?? h.status} {h.by && h.by !== 'user' ? `(${h.by})` : ''}{h.reason ? ` · ${h.reason}` : ''}
                </div>
            ))}
        </div>
    );
}

export default function SupportTickets() {
    const [snap, loading, error] = useCollection(query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc')));
    const [kind, setKind] = useState<'problem' | 'suggestion'>('problem');
    const [show, setShow] = useState<'active' | 'done' | 'all'>('active');
    const [role, setRole] = useState<'all' | 'buyer' | 'seller'>('all');
    const [category, setCategory] = useState<string>('all');
    const [selected, setSelected] = useState<string | null>(null);

    const all = useMemo(() => snap?.docs ?? [], [snap]);
    const counts = useMemo(() => ({
        problem: all.filter((d) => d.data().type === 'problem' && !DONE_STATES.has(d.data().status)).length,
        suggestion: all.filter((d) => d.data().type === 'suggestion' && !DONE_STATES.has(d.data().status)).length,
    }), [all]);

    const rows = all
        .filter((d) => d.data().type === kind)
        .filter((d) => show === 'all' || (show === 'done') === DONE_STATES.has(d.data().status))
        .filter((d) => role === 'all' || d.data().userRole === role)
        .filter((d) => category === 'all' || d.data().category === category)
        // Active: oldest first so nothing waits forever.
        .sort((a, b) => (show === 'active' ? -1 : 1) * ((b.data().createdAt?.toMillis?.() ?? 0) - (a.data().createdAt?.toMillis?.() ?? 0)));

    const sel = all.find((d) => d.id === selected);

    return (
        <div>
            <div style={{display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap'}}>
                {(['problem', 'suggestion'] as const).map((k) => (
                    <button key={k} onClick={() => { setKind(k); setCategory('all'); setSelected(null); }}
                            style={{...btn, background: kind === k ? '#29327b' : '#eef2ff', color: kind === k ? '#fff' : '#29327b'}}>
                        {k === 'problem' ? 'Problems' : 'Suggestions'} ({counts[k]})
                    </button>
                ))}
                <select value={show} onChange={(e) => setShow(e.target.value as any)} style={sel_}>
                    <option value="active">Active</option><option value="done">Finished</option><option value="all">All</option>
                </select>
                <select value={role} onChange={(e) => setRole(e.target.value as any)} style={sel_}>
                    <option value="all">Buyers & sellers</option><option value="buyer">Buyers</option><option value="seller">Sellers</option>
                </select>
                {kind === 'problem' && (
                    <select value={category} onChange={(e) => setCategory(e.target.value)} style={sel_}>
                        <option value="all">All categories</option>
                        {Object.entries(CATEGORY_LABEL).filter(([k]) => k !== 'suggestion').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                )}
            </div>
            <div style={{display: 'flex', gap: 14, flexWrap: 'wrap'}}>
                <div style={{flex: '1 1 320px', maxWidth: 420}}>
                    {loading && <div style={{padding: 20, color: '#999'}}>Loading…</div>}
                    {error && <div style={{padding: 20, color: '#b91c1c'}}>{error.message}</div>}
                    {!loading && rows.length === 0 && <div style={{padding: 20, color: '#999'}}>Nothing here.</div>}
                    {rows.map((d) => {
                        const t: any = d.data();
                        return (
                            <div key={d.id} onClick={() => setSelected(d.id)}
                                 style={{padding: 10, borderRadius: 10, marginBottom: 6, cursor: 'pointer', background: '#fff',
                                     border: selected === d.id ? '1px solid #29327b' : '1px solid #eee'}}>
                                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                    <b style={{fontSize: 13}}>#{t.ticketNo} · {CATEGORY_LABEL[t.category] ?? t.category}</b>
                                    <span style={{fontSize: 11, color: '#999'}}>{age(t.createdAt)}</span>
                                </div>
                                <div style={{fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{t.description}</div>
                                <div style={{marginTop: 4}}>
                                    <Tag color={t.userRole === 'seller' ? 'purple' : 'cyan'}>{t.userRole === 'seller' ? 'Seller' : 'Buyer'}</Tag>
                                    <Tag color={STATUS_COLOR[t.status]}>{STATUS_LABEL[t.status]}</Tag>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div style={{flex: '2 1 420px'}}>
                    {sel ? <TicketDetail id={sel.id} t={sel.data()}/>
                        : <div style={{padding: 40, color: '#999', textAlign: 'center', border: '1px dashed #eee', borderRadius: 10}}>Select a ticket</div>}
                </div>
            </div>
        </div>
    );
}

const sel_: React.CSSProperties = {fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #ddd'};
